import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Modal } from '../../../../components/Modal/Modal'
import { type AccountSchedulingSettings, EventTypeAccess, type Location, MeetingMode, type SchedulingEventType } from '../../../../models/Scheduling'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { PlaceToggleCards } from '../../../Scheduling/flows/form/PlaceToggleCards'
import { DURATION_OPTIONS, eventTypeLocationPatch, eventTypeLocationSelectValue } from '../../../Scheduling/shared/eventTypeOptions'
import { EventTypeLocationField } from '../../../Scheduling/shared/EventTypeLocationField'
import {
  isPartialRedtailSelection,
  ownRedtailEntry,
  toRedtailEventConfig,
  toRedtailSelection,
  withOwnRedtailEntry,
} from '../../../Scheduling/shared/eventConfig'
import { RedtailActivityMatchFields } from '../../../Scheduling/shared/RedtailActivityMatchFields'
import { useRedtailEntityOptions, useRedtailEventTypeAccess } from '../../../Scheduling/shared/useRedtailEventConfig'
import styles from '../../../Scheduling/shared/EventTypeModal.module.scss'

interface EditEventTypeModalProps {
  eventType: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  locations: Location[]
  isSaving: boolean
  onSave: (patch: Partial<SchedulingEventType>) => void
  onCancel: () => void
}

const COPY: Record<EventTypeAccess, { title: string; subtitle: string }> = {
  [EventTypeAccess.ACCOUNT]: {
    title: 'Edit shared event type',
    subtitle: 'Visible to everyone in your account. Uses account defaults you can change later.',
  },
  [EventTypeAccess.PERSONAL]: {
    title: 'Edit personal event type',
    subtitle: 'Only you can offer this event type. Uses account defaults you can change later.',
  },
}

export const EditEventTypeModal: FC<EditEventTypeModalProps> = ({ eventType, accountSettings, locations, isSaving, onSave, onCancel }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const redtailAccess = useRedtailEventTypeAccess(accountId, eventType.access)
  const { activityCodeOptions, categoryOptions } = useRedtailEntityOptions(accountId, redtailAccess.enabled)
  const ownEntry = redtailAccess.enabled ? ownRedtailEntry(eventType.eventConfig, redtailAccess.redtailAccountId) : undefined

  const [name, setName] = useState(eventType.name)
  const [durationMinutes, setDurationMinutes] = useState(eventType.durationMinutes)
  const [meetingMode, setMeetingMode] = useState<MeetingMode>(eventType.meetingMode)
  const [locationValue, setLocationValue] = useState(eventTypeLocationSelectValue(eventType))
  const [description, setDescription] = useState(eventType.description ?? '')
  const [activityCodeOverride, setActivityCodeId] = useState<string>()
  const [categoryOverride, setCategoryId] = useState<string>()
  const activityCodeId = activityCodeOverride ?? toRedtailSelection(ownEntry?.activityCodeId)
  const categoryId = categoryOverride ?? toRedtailSelection(ownEntry?.categoryId)

  const defaultLocation = locations.find((location) => location.id === accountSettings?.defaults.locationId)
  const isInPerson = meetingMode === MeetingMode.IN_PERSON
  const copy = COPY[eventType.access]

  const handleSave = () => {
    onSave({
      name: name.trim(),
      durationMinutes,
      meetingMode,
      location: isInPerson ? eventTypeLocationPatch(locationValue).location : null,
      description,
      ...(redtailAccess.enabled
        ? { eventConfig: withOwnRedtailEntry(eventType.eventConfig, redtailAccess.redtailAccountId, toRedtailEventConfig(activityCodeId, categoryId)) }
        : {}),
    })
  }

  return (
    <Modal
      show
      dialogClassName={styles.dialog}
      onHide={onCancel}
      header={
        <div className='d-flex flex-column zocks-gap-0.25'>
          <h5 className='mb-0'>{copy.title}</h5>
          <span className='paragraph-small zocks-text-gray-600'>{copy.subtitle}</span>
        </div>
      }
      headerBodySeparator
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='edit-event-type-name'>
              Name <span className='zocks-text-danger'>*</span>
            </label>
            <span className='paragraph-small zocks-text-gray-600'>Clients see this when they book.</span>
            <Form.Control id='edit-event-type-name' required type='text' value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </div>
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='edit-event-type-duration'>
              Duration
            </label>
            <Form.Select id='edit-event-type-duration' value={durationMinutes} onChange={(event) => setDurationMinutes(Number(event.currentTarget.value))}>
              {DURATION_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} minutes
                </option>
              ))}
            </Form.Select>
          </div>
          <PlaceToggleCards
            meetingMode={meetingMode}
            defaultLocation={defaultLocation}
            onChange={(mode, locId) => {
              setMeetingMode(mode)
              setLocationValue(locId ?? '')
            }}
          />
          {isInPerson ? <EventTypeLocationField id='edit-event-type-location' locations={locations} value={locationValue} onChange={setLocationValue} /> : null}
          <div className='d-flex flex-column zocks-gap-0.25'>
            <label className='bold' htmlFor='edit-event-type-description'>
              Description <span className='zocks-text-gray-500'>(optional)</span>
            </label>
            <span className='paragraph-small zocks-text-gray-600'>Only your team sees this.</span>
            <Form.Control
              as='textarea'
              id='edit-event-type-description'
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
            />
          </div>
          {redtailAccess.enabled ? (
            <RedtailActivityMatchFields
              idPrefix='edit-event-type'
              activityCodeId={activityCodeId}
              categoryId={categoryId}
              activityCodeOptions={activityCodeOptions}
              categoryOptions={categoryOptions}
              onActivityCodeChange={setActivityCodeId}
              onCategoryChange={setCategoryId}
            />
          ) : null}
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle='Save changes'
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={
        !name.trim() || (isInPerson && !locationValue) || (redtailAccess.enabled && isPartialRedtailSelection(activityCodeId, categoryId))
      }
      footerPrimaryActionLoading={isSaving}
      onFooterPrimaryAction={handleSave}
      onFooterSecondaryAction={onCancel}
    />
  )
}
